/**
 * @description Hook customizado para gerenciar autenticação com Google OAuth
 * @module useGoogleAuth
 * @since Novembro 2025
 * 
 * Centraliza toda a lógica de integração com Google OAuth:
 * - Carregamento dos scripts do Google
 * - Inicialização do cliente OAuth
 * - Handlers de login e logout
 * - Gerenciamento de tokens
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { DeviceIDPatch } from "@/services/scim/profile/DeviceIDPatch";

// Escopos necessários para a integração com Google Calendar
const GOOGLE_SCOPES = [
  "https://www.googleapis.com/auth/calendar.events", // Permite ver, editar e criar eventos
  "https://www.googleapis.com/auth/userinfo.email", // Permite obter o email do usuário
];

// ----- Fluxo mobile (WebView do app React Native) -----
// Dentro da WebView, o popup do Google é bloqueado (disallowed_useragent). Por
// isso o OAuth é aberto no navegador do sistema via ponte com o app. Esse fluxo
// usa PKCE (sem client_secret) e um redirect por deep link do app.
const GOOGLE_MOBILE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID_MOBILE ||
  import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_MOBILE_REDIRECT_URI =
  import.meta.env.VITE_GOOGLE_MOBILE_REDIRECT_URI || "fluxmed://oauth2redirect";

/** Codifica bytes em base64url (sem '+', '/' e '='), formato exigido pelo PKCE. */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Gera o par PKCE: um verifier aleatório e o seu challenge (SHA-256). */
async function generatePkcePair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const randomBytes = new Uint8Array(32);
  crypto.getRandomValues(randomBytes);
  const verifier = base64UrlEncode(randomBytes.buffer);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier)
  );
  const challenge = base64UrlEncode(digest);
  return { verifier, challenge };
}

/** Detecta se a aplicação está rodando dentro da WebView do app mobile. */
function isReactNativeWebView(): boolean {
  return (
    typeof window !== "undefined" &&
    Boolean((window as any).ReactNativeWebView)
  );
}

// Declaração global para os objetos do Google
declare global {
  interface Window {
    gapi: any;
    tokenClient: any;
    google: any;
    googlePopupTimeout: NodeJS.Timeout;
  }
}

interface UseGoogleAuthReturn {
  /** Indica se está conectado ao Google */
  isConnected: boolean;
  /** Indica se está em processo de loading (login/logout) */
  isLoading: boolean;
  /** Inicia o processo de login com Google */
  handleLogin: () => void;
  /** Inicia o processo de logout do Google */
  handleLogout: () => Promise<void>;
  /** Toggle entre login e logout baseado no estado atual */
  handleToggleAuth: () => void;
}

export function useGoogleAuth(): UseGoogleAuthReturn {
  const { user, fetchUser, setAccessTokenGoogle } = useSessionContext();
  const [isLoading, setIsLoading] = useState(false);
  // Guarda o code_verifier do PKCE entre o início do login (mobile) e a volta do code.
  const codeVerifierRef = useRef<string>("");

  // Verifica se está conectado baseado no refreshToken do usuário
  const isConnected = Boolean(user?.refreshTokenGoogle);

  /**
   * Persiste os tokens do Google: salva o refresh token no usuário e o access
   * token no localStorage. Compartilhado pelos fluxos web (popup) e mobile.
   */
  const persistGoogleTokens = useCallback(
    async (tokens: any) => {
      if (tokens?.refresh_token) {
        // Salva o refresh token no usuário
        await DeviceIDPatch({
          idUser: user?.id,
          operation: "replace",
          pathName:
            "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_refresh_token",
          valueField: tokens.refresh_token,
        });

        setAccessTokenGoogle(tokens.access_token);

        // Armazena o token no localStorage
        localStorage.setItem("googleAccessToken", tokens.access_token);

        // Atualiza os dados do usuário
        fetchUser();
      } else {
        console.warn(
          "Refresh token não recebido. Tente remover o acesso do app e tentar novamente."
        );
      }
    },
    [user?.id, fetchUser, setAccessTokenGoogle]
  );

  /**
   * Processa a resposta do token do Google (fluxo web / popup, com client_secret)
   */
  const handleTokenResponse = useCallback(
    async (response: any) => {
      try {
        if (response.code) {
          // Troca o código por tokens
          const tokenResponse = await fetch(
            "https://oauth2.googleapis.com/token",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({
                client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
                client_secret: import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
                code: response.code,
                grant_type: "authorization_code",
                access_type: "offline",
                redirect_uri: window.location.origin,
              }),
            }
          );

          if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json();
            console.error("Erro ao trocar código por tokens:", errorData);
            throw new Error("Falha ao obter tokens");
          }

          const tokens = await tokenResponse.json();
          await persistGoogleTokens(tokens);
        }
      } catch (error) {
        console.error("Erro ao processar resposta:", error);
        alert("Erro ao fazer login. Por favor, tente novamente.");
      } finally {
        setIsLoading(false);
      }
    },
    [persistGoogleTokens]
  );

  /**
   * Troca o `code` recebido do app (via WebView) por tokens usando PKCE.
   * Clientes OAuth mobile (Android/iOS) são públicos: NÃO usam client_secret.
   */
  const exchangeMobileCode = useCallback(
    async (code: string) => {
      try {
        const tokenResponse = await fetch(
          "https://oauth2.googleapis.com/token",
          {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
              client_id: GOOGLE_MOBILE_CLIENT_ID,
              code,
              code_verifier: codeVerifierRef.current,
              grant_type: "authorization_code",
              redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
            }),
          }
        );

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          console.error(
            "Erro ao trocar código por tokens (mobile):",
            errorData
          );
          throw new Error("Falha ao obter tokens (mobile)");
        }

        const tokens = await tokenResponse.json();
        await persistGoogleTokens(tokens);
      } catch (error) {
        console.error("Erro ao processar resposta (mobile):", error);
      } finally {
        // Limpa o verifier para não reagir a mensagens de outros fluxos depois.
        codeVerifierRef.current = "";
        setIsLoading(false);
      }
    },
    [persistGoogleTokens]
  );

  /**
   * Inicializa o cliente do Google OAuth
   */
  const initializeGoogleClient = useCallback(() => {
    if (!window.google) return;

    window.tokenClient = window.google.accounts.oauth2.initCodeClient({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      scope: GOOGLE_SCOPES.join(" "),
      callback: (response: any) => {
        if (window.googlePopupTimeout) {
          clearTimeout(window.googlePopupTimeout);
        }
        handleTokenResponse(response);
      },
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: true,
      select_account: false,
      ux_mode: "popup",
    });

    console.log("Cliente Google inicializado com sucesso");
  }, [handleTokenResponse]);

  /**
   * Carrega os scripts do Google e inicializa o cliente
   */
  useEffect(() => {
    const loadGoogleScripts = () => {
      // Carrega o script do Google Identity Services
      const gsiScript = document.createElement("script");
      gsiScript.src = "https://accounts.google.com/gsi/client";
      gsiScript.async = true;
      gsiScript.defer = true;
      gsiScript.onload = () => {
        console.log("GSI carregado, inicializando cliente...");
        initializeGoogleClient();
      };
      document.head.appendChild(gsiScript);

      // Carrega o script do GAPI
      const gapiScript = document.createElement("script");
      gapiScript.src = "https://apis.google.com/js/api.js";
      gapiScript.async = true;
      gapiScript.defer = true;
      document.head.appendChild(gapiScript);
    };

    // Handler para mensagens de cancelamento do Google
    const handleGoogleMessage = (event: MessageEvent) => {
      if (
        event.origin.includes("accounts.google.com") &&
        event.data &&
        (event.data.type === "cancel" || event.data.type === "close")
      ) {
        setIsLoading(false);
        console.log("Autenticação do Google cancelada pelo usuário");
      }
    };

    window.addEventListener("message", handleGoogleMessage);
    loadGoogleScripts();

    // Cleanup
    return () => {
      const scripts = document.head.getElementsByTagName("script");
      Array.from(scripts).forEach((script) => {
        if (
          script.src.includes("accounts.google.com") ||
          script.src.includes("apis.google.com")
        ) {
          script.remove();
        }
      });
      window.removeEventListener("message", handleGoogleMessage);
    };
  }, [initializeGoogleClient]);

  /**
   * Escuta as mensagens vindas do app mobile (via WebView) com o resultado do
   * OAuth aberto no navegador do sistema: o `code` (sucesso) ou um erro/cancelamento.
   * O app envia via injectJavaScript, que dispara um MessageEvent com `data` string.
   */
  useEffect(() => {
    const handleNativeAuthMessage = (event: MessageEvent) => {
      let data: any = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return; // Não é uma mensagem JSON nossa.
        }
      }
      if (!data || typeof data !== "object") return;

      if (data.type === "GOOGLE_OAUTH_CODE" && data.payload?.code) {
        // Só troca o code se este hook iniciou o fluxo (tem verifier salvo).
        // Evita conflito com o hook do Google Fit, que ouve a mesma mensagem.
        if (!codeVerifierRef.current) return;
        exchangeMobileCode(data.payload.code);
      } else if (data.type === "GOOGLE_OAUTH_ERROR") {
        // Só reage ao erro se este hook iniciou o fluxo.
        if (!codeVerifierRef.current) return;
        codeVerifierRef.current = "";
        setIsLoading(false);
        if (data.payload?.message && data.payload.message !== "cancelled") {
          console.warn("Login Google (mobile) falhou:", data.payload.message);
        }
      }
    };

    window.addEventListener("message", handleNativeAuthMessage);
    return () =>
      window.removeEventListener("message", handleNativeAuthMessage);
  }, [exchangeMobileCode]);

  /**
   * Inicia o processo de login com Google
   */
  const handleLogin = useCallback(async () => {
    // Dentro da WebView do app mobile: abre o OAuth no navegador do sistema
    // (via ponte com o React Native), pois o Google bloqueia OAuth em WebView.
    if (isReactNativeWebView()) {
      try {
        setIsLoading(true);
        console.log("Iniciando login Google (mobile)...");

        // Gera o par PKCE e guarda o verifier para a troca do code depois.
        const { verifier, challenge } = await generatePkcePair();
        codeVerifierRef.current = verifier;

        // Monta a URL de consentimento do Google (fluxo authorization code + PKCE).
        const authUrl =
          "https://accounts.google.com/o/oauth2/v2/auth?" +
          new URLSearchParams({
            client_id: GOOGLE_MOBILE_CLIENT_ID,
            redirect_uri: GOOGLE_MOBILE_REDIRECT_URI,
            response_type: "code",
            access_type: "offline",
            prompt: "consent",
            include_granted_scopes: "true",
            code_challenge: challenge,
            code_challenge_method: "S256",
            scope: GOOGLE_SCOPES.join(" "),
          }).toString();

        // Pede ao app para abrir a URL no navegador do sistema.
        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "GOOGLE_OAUTH",
            payload: { url: authUrl, redirectUri: GOOGLE_MOBILE_REDIRECT_URI },
          })
        );
      } catch (error) {
        console.error("Erro ao iniciar login (mobile):", error);
        setIsLoading(false);
      }
      return;
    }

    // Fluxo web padrão (popup do Google Identity Services).
    if (!window.tokenClient) {
      console.error("Cliente do Google não está inicializado");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Iniciando processo de login...");

      // Timeout para resetar loading caso o popup seja fechado
      const popupTimeout = setTimeout(() => {
        setIsLoading(false);
        console.log("Timeout de autenticação - possivelmente o popup foi fechado");
      }, 60000); // 1 minuto de timeout

      window.googlePopupTimeout = popupTimeout;
      window.tokenClient.requestCode();
    } catch (error) {
      console.error("Erro ao solicitar autorização:", error);
      setIsLoading(false);
      alert("Erro ao fazer login. Por favor, tente novamente.");
    }
  }, []);

  /**
   * Inicia o processo de logout do Google
   */
  const handleLogout = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("Iniciando logout...");

      // Remove o refresh token do usuário
      await DeviceIDPatch({
        idUser: user?.id,
        operation: "replace",
        pathName:
          "urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:google_refresh_token",
        valueField: "",
      });

      // Limpa o token do localStorage
      localStorage.removeItem("googleAccessToken");

      // Atualiza os dados do usuário
      fetchUser();
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, fetchUser]);

  /**
   * Toggle entre login e logout
   */
  const handleToggleAuth = useCallback(() => {
    if (isConnected) {
      handleLogout();
    } else {
      handleLogin();
    }
  }, [isConnected, handleLogin, handleLogout]);

  return {
    isConnected,
    isLoading,
    handleLogin,
    handleLogout,
    handleToggleAuth,
  };
}

